<template>
  <div class="fixed bottom-6 right-6 z-50">
    <!-- Notification Bell Button -->
    <button
      @click="showNotifications = !showNotifications"
      class="relative bg-primary-600 text-white p-4 rounded-full shadow-lg hover:bg-primary-700 transition-all hover:scale-110"
    >
      <BellIcon class="w-6 h-6" />
      <span
        v-if="unreadCount > 0"
        class="absolute -top-1 -right-1 bg-danger-500 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center font-bold"
      >
        {{ unreadCount > 9 ? '9+' : unreadCount }}
      </span>
    </button>
    
    <!-- Notifications Panel -->
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="showNotifications"
        class="absolute bottom-20 right-0 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
      >
        <!-- Header -->
        <div class="bg-primary-600 text-white px-4 py-3 flex items-center justify-between">
          <h3 class="font-semibold">Notifications</h3>
          <button
            @click="markAllAsRead"
            class="text-sm hover:underline"
          >
            Mark all as read
          </button>
        </div>
        
        <!-- Notifications List -->
        <div class="max-h-96 overflow-y-auto">
          <div
            v-if="notifications.length === 0"
            class="p-8 text-center text-gray-500"
          >
            <BellSlashIcon class="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No notifications yet</p>
          </div>
          
          <div
            v-else
            v-for="notification in notifications"
            :key="notification.id"
            :class="[
              'p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer',
              !notification.read ? 'bg-primary-50' : ''
            ]"
            @click="handleNotificationClick(notification)"
          >
            <div class="flex items-start">
              <div
                :class="[
                  'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                  getSeverityColor(notification.severity)
                ]"
              >
                <component :is="getNotificationIcon(notification.type)" class="w-5 h-5 text-white" />
              </div>
              <div class="ml-3 flex-1">
                <p class="text-sm font-medium text-gray-900">
                  {{ notification.title }}
                </p>
                <p class="text-sm text-gray-500 mt-1">
                  {{ notification.message }}
                </p>
                <p class="text-xs text-gray-400 mt-1">
                  {{ formatTime(notification.timestamp) }}
                </p>
              </div>
              <div
                v-if="!notification.read"
                class="w-2 h-2 bg-primary-600 rounded-full"
              ></div>
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="p-3 bg-gray-50 text-center">
          <RouterLink
            to="/alerts"
            class="text-sm text-primary-600 hover:text-primary-700 font-medium"
            @click="showNotifications = false"
          >
            View all alerts →
          </RouterLink>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { RouterLink } from 'vue-router'
import {
  BellIcon,
  BellSlashIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  PhoneIcon,
  CalendarIcon
} from '@heroicons/vue/24/outline'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useAlertStore } from '@/stores/alerts'

dayjs.extend(relativeTime)

const showNotifications = ref(false)

// Use real notifications from alerts store
const alertStore = useAlertStore()
const notifications = computed(() => {
  // Convert alerts to notification format
  return alertStore.alerts.slice(0, 10).map(alert => ({
    id: alert.id,
    type: alert.type || 'alert',
    severity: alert.severity || 'medium',
    title: `${alert.type?.charAt(0).toUpperCase() + alert.type?.slice(1)} Alert`,
    message: alert.message,
    timestamp: new Date(alert.created_at),
    read: alert.is_resolved || false
  }))
})

const unreadCount = computed(() => {
  return notifications.value.filter(n => !n.read).length
})

function getNotificationIcon(type) {
  const icons = {
    alert: ExclamationTriangleIcon,
    wellness: HeartIcon,
    call: PhoneIcon,
    schedule: CalendarIcon
  }
  return icons[type] || BellIcon
}

function getSeverityColor(severity) {
  const colors = {
    critical: 'bg-danger-500',
    high: 'bg-warning-500',
    medium: 'bg-primary-500',
    low: 'bg-success-500'
  }
  return colors[severity] || 'bg-gray-500'
}

function formatTime(timestamp) {
  return dayjs(timestamp).fromNow()
}

function handleNotificationClick(notification) {
  notification.read = true
  // Navigate to relevant page based on notification type
}

function markAllAsRead() {
  notifications.value.forEach(n => {
    n.read = true
  })
}

// WebSocket connection for real-time notifications
let ws = null

onMounted(() => {
  // Connect to WebSocket for real-time notifications
  // ws = new WebSocket('ws://localhost:5000/ws')
  // ws.onmessage = (event) => {
  //   const notification = JSON.parse(event.data)
  //   notifications.value.unshift(notification)
  // }
})

onUnmounted(() => {
  if (ws) {
    ws.close()
  }
})
</script>